// src/components/Profile.js

import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase';
import api from '../api';
import { useSnackbar } from 'notistack';
import {
  updateProfile,
  updateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendPasswordResetEmail,
} from 'firebase/auth';
import {
  TextField,
  Button,
  Typography,
  Container,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Link,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LoadingState from './LoadingState';

function Profile() {
    const [displayName, setDisplayName] = useState('');
    const [email, setEmail] = useState('');
    const [originalEmail, setOriginalEmail] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [giftProfile, setGiftProfile] = useState({
      clothingSize: '',
      shoeSize: '',
      colourPreferences: '',
      brandPreferences: '',
      otherNotes: '',
    });

    // API key state
    const [apiKeys, setApiKeys] = useState([]);
    const [newKeyName, setNewKeyName] = useState('');
    const [generatedKey, setGeneratedKey] = useState('');
    const [apiKeyLoading, setApiKeyLoading] = useState(false);

    const fetchApiKeys = useCallback(async () => {
      try {
        const res = await api.get('/auth/api-keys');
        setApiKeys(res.data);
      } catch (error) {
        console.error('Error fetching API keys:', error);
      }
    }, []);

    useEffect(() => {
      const user = auth.currentUser;
      if (user) {
        setDisplayName(user.displayName || '');
        setEmail(user.email || '');
        setOriginalEmail(user.email || '');
      }
      // Fetch current user data
      const fetchUser = async () => {
        try {
          const res = await api.get('/users/profile');
          if (res.data?.giftProfile) {
            setGiftProfile({
              clothingSize: res.data.giftProfile.clothingSize || '',
              shoeSize: res.data.giftProfile.shoeSize || '',
              colourPreferences: res.data.giftProfile.colourPreferences || '',
              brandPreferences: res.data.giftProfile.brandPreferences || '',
              otherNotes: res.data.giftProfile.otherNotes || '',
            });
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          enqueueSnackbar('Failed to load profile.', { variant: 'error' });
        } finally {
          setLoading(false);
        }
      };
      fetchUser();
      fetchApiKeys();
    }, [enqueueSnackbar, fetchApiKeys]);

    const handleGenerateKey = async () => {
      if (!newKeyName.trim()) return;
      setApiKeyLoading(true);
      try {
        const res = await api.post('/auth/api-keys', { name: newKeyName.trim() });
        setGeneratedKey(res.data.rawKey);
        setNewKeyName('');
        await fetchApiKeys();
      } catch (error) {
        console.error('Error generating API key:', error);
        enqueueSnackbar('Failed to generate API key.', { variant: 'error' });
      } finally {
        setApiKeyLoading(false);
      }
    };

    const handleDeleteKey = async (keyId) => {
      try {
        await api.delete(`/auth/api-keys/${keyId}`);
        setApiKeys((prev) => prev.filter((k) => k._id !== keyId));
        enqueueSnackbar('API key deleted.', { variant: 'success' });
      } catch (error) {
        console.error('Error deleting API key:', error);
        enqueueSnackbar('Failed to delete API key.', { variant: 'error' });
      }
    };

    const handleCopyKey = () => {
      navigator.clipboard.writeText(generatedKey);
      enqueueSnackbar('API key copied to clipboard.', { variant: 'success' });
    };

    if (loading) {
      return <LoadingState message="Loading your profile..." minHeight={320} />;
    }

    const handleSave = async () => {
      setError('');
      setSuccess('');
      const user = auth.currentUser;

      try {
        if (user) {
          // Update display name
          if (user.displayName !== displayName) {
            const name = displayName;
            await api.put('/users/profile', { name });
            await updateProfile(user, { displayName });
          }

          // Update email
          if (user.email !== email) {
            // Re-authenticate user before updating email
            setPasswordDialogOpen(true);
          } else {
            setSuccess('Profile updated successfully.');
          }
        }
      } catch (error) {
        console.error('Error updating profile:', error);
        setError(error.message);
      }
    };

    const handleSaveGiftProfile = async () => {
      setError('');
      setSuccess('');
      try {
        await api.put('/users/profile', { giftProfile });
        setSuccess('Gift preferences saved.');
      } catch (error) {
        console.error('Error saving gift profile:', error);
        setError(error.message);
      }
    };

    const handleEmailUpdate = async () => {
      const user = auth.currentUser;
      if (user && currentPassword) {
        try {
          const credential = EmailAuthProvider.credential(originalEmail, currentPassword);
          await reauthenticateWithCredential(user, credential);
          await updateEmail(user, email);
          setSuccess('Profile updated successfully.');
          setPasswordDialogOpen(false);
          setCurrentPassword('');
        } catch (error) {
          console.error('Error updating email:', error);
          setError(error.message);
        }
      } else {
        setError('Please enter your current password.');
      }
    };

    const handlePasswordReset = async () => {
      try {
        await sendPasswordResetEmail(auth, email);
        setSuccess('Password reset email sent.');
      } catch (error) {
        console.error('Error sending password reset email:', error);
        setError(error.message);
      }
    };

    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Typography
          variant="h3"
          gutterBottom
          sx={{ fontFamily: '"Fraunces", Georgia, serif' }}
        >
          Profile
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        <Paper
          sx={{
            p: 4,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 4,
          }}
        >
          <Typography variant="overline" color="text.secondary">
            ACCOUNT DETAILS
          </Typography>
          <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
            <TextField
              label="Display Name"
              variant="outlined"
              margin="normal"
              fullWidth
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <TextField
              label="Email"
              variant="outlined"
              margin="normal"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Save Changes
              </Button>
              <Button variant="outlined" color="secondary" onClick={handlePasswordReset}>
                Change Password
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Gift Preferences section */}
        <Paper
          sx={{
            p: 4,
            mt: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 4,
          }}
        >
          <Typography variant="overline" color="text.secondary">
            GIFT PREFERENCES
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Help your friends and family find great gifts. Other event members can view this when browsing your list.
          </Typography>
          <Box component="form" noValidate autoComplete="off">
            <TextField
              label="Clothing size"
              placeholder="e.g. M top, 32/32 jeans"
              variant="outlined"
              margin="normal"
              fullWidth
              value={giftProfile.clothingSize}
              onChange={(e) => setGiftProfile({ ...giftProfile, clothingSize: e.target.value })}
            />
            <TextField
              label="Shoe size"
              placeholder="e.g. EU 43 / UK 9"
              variant="outlined"
              margin="normal"
              fullWidth
              value={giftProfile.shoeSize}
              onChange={(e) => setGiftProfile({ ...giftProfile, shoeSize: e.target.value })}
            />
            <TextField
              label="Colour preferences"
              placeholder="e.g. blues and greens, no orange"
              variant="outlined"
              margin="normal"
              fullWidth
              value={giftProfile.colourPreferences}
              onChange={(e) => setGiftProfile({ ...giftProfile, colourPreferences: e.target.value })}
            />
            <TextField
              label="Brand preferences"
              placeholder="e.g. Patagonia, Levi's, Arc'teryx"
              variant="outlined"
              margin="normal"
              fullWidth
              value={giftProfile.brandPreferences}
              onChange={(e) => setGiftProfile({ ...giftProfile, brandPreferences: e.target.value })}
            />
            <TextField
              label="Other notes"
              placeholder="e.g. allergic to wool, vegetarian"
              variant="outlined"
              margin="normal"
              fullWidth
              multiline
              minRows={2}
              value={giftProfile.otherNotes}
              onChange={(e) => setGiftProfile({ ...giftProfile, otherNotes: e.target.value })}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" color="primary" onClick={handleSaveGiftProfile}>
                Save Preferences
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Developer API Keys section */}
        <Paper
          sx={{
            p: 4,
            mt: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 4,
          }}
        >
          <Typography variant="overline" color="text.secondary">
            DEVELOPER API KEYS
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Use API keys to access ListLink from scripts, integrations, or AI assistants like Claude. Each key has full access to your account — keep them secret.{' '}
            <Link href="/docs" target="_blank" rel="noopener">
              API docs and MCP setup
            </Link>
          </Typography>

          {generatedKey && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setGeneratedKey('')}
              action={
                <Tooltip title="Copy key">
                  <IconButton size="small" onClick={handleCopyKey}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Your new API key — copy it now, it won't be shown again:
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
              >
                {generatedKey}
              </Typography>
            </Alert>
          )}

          {apiKeys.length > 0 && (
            <>
              <List disablePadding>
                {apiKeys.map((key, index) => (
                  <React.Fragment key={key._id}>
                    {index > 0 && <Divider component="li" />}
                    <ListItem disableGutters>
                      <ListItemText
                        primary={key.name}
                        secondary={
                          <>
                            Created {new Date(key.createdAt).toLocaleDateString()}
                            {key.lastUsedAt &&
                              ` · Last used ${new Date(key.lastUsedAt).toLocaleDateString()}`}
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Delete key">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleDeleteKey(key._id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
              <Divider sx={{ my: 2 }} />
            </>
          )}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              label="Key name"
              placeholder="e.g. Claude Desktop"
              variant="outlined"
              size="small"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleGenerateKey()}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleGenerateKey}
              disabled={!newKeyName.trim() || apiKeyLoading}
            >
              Generate
            </Button>
          </Box>
        </Paper>

        {/* Dialog for re-authentication */}
        <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
          <DialogTitle>Confirm Password</DialogTitle>
          <DialogContent>
            <Typography>Please enter your current password to update your email.</Typography>
            <TextField
              label="Current Password"
              type="password"
              fullWidth
              margin="normal"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEmailUpdate}>Confirm</Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  export default Profile;
